import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {CastOperatorTypeOption} from "#template/nodes/geometry-graph/options"
import {castOperatorTypeOption} from "#template/nodes/geometry-graph/options"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type CastOperatorParameters<TAttribute> = {
    name: string
    attribute: TAttribute | null
    type: CastOperatorTypeOption
}

export const castOperatorParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        name: z.string(),
        attribute: z.nullable(attributeSchema),
        type: castOperatorTypeOption,
    })

function applyCastOperator<T extends IndexingDim>(attribute: GeometryAttributeData<T>, typeOption: CastOperatorTypeOption): GeometryAttributeData<T> {
    const attrRefData = attribute.ref
    const castAttrRefData = applyGeometryOperator((attrRef: AttributeRef) => {
        switch (typeOption) {
            case "float":
                return attrRef.castFloat()
            case "int":
                return attrRef.castInt()
            default:
                throw new Error(`Unknown cast operator type option: ${typeOption}`)
        }
    }, attrRefData)
    return createGeometryAttributeData(castAttrRefData, attribute.dim)
}

export abstract class GeometryCastOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, CastOperatorParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: CastOperatorParameters<TAttribute>,
    ): parameters is CastOperatorParameters<TAttribute> & NonNullableParameters<CastOperatorParameters<TAttribute>> {
        return parameters.attribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<CastOperatorParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attribute, type: castTypeParameter} = parameters

        const [attributeDataValid, attributeDataInvalid] = scope.branch(evaluator.evaluate(attribute))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(attributeDataValid, castTypeParameter),
            ([attributeData, castType]) => applyCastOperator(attributeData, castType),
            "applyCastOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: attributeDataInvalid}
    }
}
